import React, { useState, useMemo, useRef } from 'react';
import { Checkbox } from 'primereact/checkbox';
import { Button } from 'primereact/button';
import { ProgramDetails } from '../../../../../../api/types/programs/program-details';
import { UserSummaryType } from '../../../../../../api/types/user/user-summary.type';
import { CourseMetadataType } from '../../../../../../api/types/programs/course/course-metadata.type';
import { ProgramSelectCourses } from '../../componenets/program-select-courses';

interface AddNewStudentFromPreviusYearsTabProps {
  selectedYear: number | null;
  program: ProgramDetails;
  selectedUsers: UserSummaryType[];
  addCourses: boolean;
  selectedCourses: CourseMetadataType[];
  visible?: boolean;
  onSelectedUsersChange: (users: UserSummaryType[]) => void;
  onAddCoursesChange: (addCourses: boolean) => void;
  onSelectedCoursesChange: (courses: CourseMetadataType[]) => void;
  onApprove: (
    selectedUsers: UserSummaryType[],
    setLoading: (loading: boolean) => void,
  ) => Promise<void>;
}

export const AddNewStudentFromPreviusYearsTab: React.FC<
  AddNewStudentFromPreviusYearsTabProps
> = ({
  selectedYear,
  program,
  selectedUsers,
  addCourses,
  selectedCourses,
  visible = true,
  onSelectedUsersChange,
  onAddCoursesChange,
  onSelectedCoursesChange,
  onApprove,
}) => {
  const [loading, setLoading] = useState(false);
  const isSubmittingRef = useRef(false);
  const [selectedFilterYear, setSelectedFilterYear] = useState<number | null>(
    null,
  );

  const getYearLabel = (year: number): string => {
    switch (year) {
      case 0:
        return 'שנה א׳';
      case 1:
        return 'שנה ב׳';
      case 2:
        return 'שנה ג׳';
      default:
        return '';
    }
  };

  const getAvailableYears = (): number[] => {
    if (selectedYear === null) return [];

    const years: number[] = [];
    for (let i = 0; i < selectedYear; i++) {
      years.push(i);
    }
    return years;
  };

  const handleYearFilterChange = (year: number, checked: boolean) => {
    if (checked) {
      setSelectedFilterYear(year);
    } else {
      setSelectedFilterYear(null);
    }
  };

  const availableYears = getAvailableYears();

  // Get students from the selected filter year
  const studentsFromSelectedYear = useMemo(() => {
    if (selectedFilterYear === null || !program.years) return [];

    const yearData = program.years.find((y) => y.year === selectedFilterYear);
    return yearData?.students || [];
  }, [selectedFilterYear, program.years]);

  // Handle student selection
  const handleStudentToggle = (
    student: UserSummaryType,
    isSelected: boolean,
  ) => {
    if (isSelected) {
      onSelectedUsersChange([...selectedUsers, student]);
    } else {
      onSelectedUsersChange(selectedUsers.filter((u) => u.id !== student.id));
    }
  };

  const handleApprove = async () => {
    // Ref blocks same-tick double clicks before React re-renders `loading`.
    if (isSubmittingRef.current || loading) return;
    isSubmittingRef.current = true;
    try {
      await onApprove(selectedUsers, setLoading);
    } finally {
      isSubmittingRef.current = false;
    }
  };

  // Filter courses based on selected year when checkbox is active
  const getCoursesToDisplay = (): CourseMetadataType[] => {
    // If dialog is not visible, return empty array to prevent flash
    if (!visible) {
      return [];
    }

    if (!addCourses || selectedYear === null) {
      return [];
    }

    // Find courses for the selected year only
    const yearData = program.years?.find((year) => year.year === selectedYear);
    const yearCourses = yearData?.courses || [];

    return yearCourses;
  };

  return (
    <div>
      {/* Message when selectedYear is 0 (שנה א׳) - no previous years available */}
      {selectedYear === 0 && (
        <div
          style={{
            textAlign: 'center',
            padding: '20px',
            backgroundColor: '#f8f9fa',
            border: '1px solid #dee2e6',
            borderRadius: '8px',
            margin: '20px 0',
          }}
        >
          <div
            style={{
              fontWeight: 'bold',
              marginBottom: '10px',
              fontSize: '16px',
            }}
          >
            לא נמצאו שנים קודמות
          </div>
          <div style={{ color: '#666', lineHeight: '1.5' }}>
            באיזור זה ניתן להוסיף סטודנטים רק משנים קודמות, לשנה זו תוכל להוסיף
            סטודנטים מהמערכת בטאב הראשון
          </div>
        </div>
      )}

      {availableYears.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <div style={{ marginBottom: '10px', fontWeight: 'bold' }}>
            בחר שנת לימודים:
          </div>
          {availableYears.map((year) => (
            <div
              key={year}
              style={{
                display: 'flex',
                flexDirection: 'row',
                marginBottom: '8px',
              }}
            >
              <Checkbox
                checked={selectedFilterYear === year}
                onChange={(e) =>
                  handleYearFilterChange(year, e.checked || false)
                }
              />
              <label style={{ marginRight: '8px' }}>{getYearLabel(year)}</label>
            </div>
          ))}
        </div>
      )}

      {/* Student list from selected year */}
      {selectedFilterYear !== null && studentsFromSelectedYear.length > 0 && (
        <div style={{ marginBottom: '20px' }}>
          <div style={{ marginBottom: '10px', fontWeight: 'bold' }}>
            סטודנטים מ{getYearLabel(selectedFilterYear)}:
          </div>
          {studentsFromSelectedYear.map((student) => (
            <div
              key={student.id}
              style={{
                display: 'flex',
                flexDirection: 'row',
                marginBottom: '8px',
                padding: '8px',
                border: '1px solid #ddd',
                borderRadius: '4px',
              }}
            >
              <Checkbox
                checked={selectedUsers.some((u) => u.id === student.id)}
                onChange={(e) =>
                  handleStudentToggle(student, e.checked || false)
                }
              />
              <div style={{ marginRight: '8px' }}>
                <div style={{ fontWeight: 'bold' }}>
                  {student.firstName} {student.lastName}
                </div>
                <div style={{ fontSize: '12px', color: '#666' }}>
                  {student.email}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Empty state */}
      {selectedFilterYear !== null && studentsFromSelectedYear.length === 0 && (
        <div
          style={{ textAlign: 'center', color: '#666', marginBottom: '20px' }}
        >
          אין סטודנטים ב{getYearLabel(selectedFilterYear)}
        </div>
      )}

      {/* Add courses checkbox - only show when students are selected */}
      {selectedUsers.length > 0 && (
        <>
          <div
            style={{
              display: 'flex',
              flexDirection: 'row',
              marginBottom: '16px',
            }}
          >
            <Checkbox
              checked={addCourses}
              onChange={(e) => onAddCoursesChange(e.checked || false)}
            />
            <label style={{ marginRight: '4px' }}>
              הוספת הסטודנטים לקורס הקיימים
            </label>
          </div>
          {addCourses && (
            <div style={{ marginBottom: '16px' }}>
              <ProgramSelectCourses
                onSelectChange={onSelectedCoursesChange}
                courses={getCoursesToDisplay()}
              />
            </div>
          )}
        </>
      )}

      {/* Approve button */}
      {selectedUsers.length > 0 && (
        <div style={{ textAlign: 'left', marginTop: '20px' }}>
          <Button
            onClick={handleApprove}
            style={{ height: '30px' }}
            label={`אישור (${selectedUsers.length} סטודנטים)`}
            loading={loading}
            disabled={loading}
          />
        </div>
      )}
    </div>
  );
};
